import { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ImageLibraryModal } from "./ImageLibraryModal";
import { ImageStylePanel } from "./ImageStylePanel";
import { useState } from "react";
import { Image as ImageIcon, MousePointerClick, Sliders } from "lucide-react";
import { toast } from "sonner";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { isSafeCtaUrl } from "@/components/ui/RichHtml";

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Advanced WYSIWYG Editor using TinyMCE
 * Features: Image library, upload, HTML mode, full formatting, embeds
 */
export function AdvancedEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showCtaDialog, setShowCtaDialog] = useState(false);
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [selectedImageNode, setSelectedImageNode] = useState(null);
  const [showImageStyle, setShowImageStyle] = useState(false);
  const [activeImageNode, setActiveImageNode] = useState(null);
  const { styles: siteStyles } = useSiteStyles();

  // Handle image selection from library
  const handleImageLibrarySelect = (imageUrl) => {
    if (editorRef.current) {
      editorRef.current.insertContent(`<img src="${imageUrl}" alt="Image" style="max-width: 100%;" />`);
    }
  };

  // Email clients can't run JS and strip <style> blocks/CSS variables, so
  // unlike the page CTA block (TiptapEditor's `slh-cta-button`, styled via
  // var(--button-bg)) this needs every colour baked in as a literal hex at
  // insert time, plus the table-based "bulletproof button" markup Outlook
  // needs to render a coloured, padded button at all. Copy/URL are HTML-
  // escaped and the URL's scheme checked before building the snippet, so
  // typed markup/quotes can't break out of the anchor and javascript:
  // URLs can't sneak into a sent email.
  const insertCtaButton = () => {
    const text = ctaText.trim();
    const url = ctaUrl.trim();
    if (!text || !url) {
      toast.error('Button copy and URL are both required');
      return;
    }
    if (!isSafeCtaUrl(url)) {
      toast.error('Enter a valid http(s), mailto, tel, or relative URL');
      return;
    }
    const bg = siteStyles?.buttonBgHex || '#2f4f4f';
    const color = siteStyles?.buttonTextHex || '#ffffff';
    const html = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 24px auto;"><tr><td align="center" bgcolor="${bg}" style="border-radius: 6px;"><a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 14px 28px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold; color: ${color}; text-decoration: none; border-radius: 6px;">${escapeHtml(text)}</a></td></tr></table>`;
    editorRef.current?.insertContent(html);
    setShowCtaDialog(false);
    setCtaText('');
    setCtaUrl('');
  };

  // Brand "Heading" + "Heading Accent" colours (from Site Styles) appear first
  // in the text colour picker so admins can quickly recreate two-colour
  // headings like "Sell your property" / "without an estate agent".
  const brandHex = (hex, fallback) => (hex || fallback).replace('#', '').toUpperCase();
  const colorMap = [
    brandHex(siteStyles?.headingHex, '1A1A1A'), 'Heading',
    brandHex(siteStyles?.headingAccentHex, 'E07B5D'), 'Heading Accent',
    'BFEDD2', 'Light Green', 'FBEEB8', 'Light Yellow', 'F8CAC6', 'Light Red',
    'ECCAFA', 'Light Purple', 'C2E0F4', 'Light Blue',
    '2DC26B', 'Green', 'F1C40F', 'Yellow', 'E03E2D', 'Red', 'B96AD9', 'Purple', '3598DB', 'Blue',
    '169179', 'Dark Turquoise', 'E67E23', 'Orange', 'BA372A', 'Dark Red', '843FA1', 'Dark Purple', '236FA1', 'Dark Blue',
    'ECF0F1', 'Light Gray', 'CED4D9', 'Medium Gray', '95A5A6', 'Gray', '7E8C8D', 'Dark Gray', '34495E', 'Navy Blue',
    '000000', 'Black', 'FFFFFF', 'White',
  ];

  // TinyMCE configuration
  const editorConfig = {
    height: 500,
    menubar: true,
    promotion: false,
    branding: false,
    color_map: colorMap,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'wordcount', 'codesample'
    ],
    toolbar: 'undo redo | blocks | bold italic underline strikethrough | ' +
      'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
      'bullist numlist outdent indent | removeformat | image media link | code',

    // Drag-resize handles on images, media embeds and tables (default is
    // already `true`, kept explicit here). `alignleft`/`aligncenter`/
    // `alignright` in the toolbar above also float/centre whichever image
    // or media embed is currently selected, not just paragraph text.
    object_resizing: 'img,video,table,.mce-object',

    // Content styling
    content_style: `
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
        font-size: 16px;
        line-height: 1.6;
        padding: 20px;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      iframe {
        max-width: 100%;
      }
      img[style*="float: left"], .mce-object[style*="float: left"] {
        margin: 0.25rem 1.5rem 1rem 0;
      }
      img[style*="float: right"], .mce-object[style*="float: right"] {
        margin: 0.25rem 0 1rem 1.5rem;
      }
    `,

    // Image upload handling
    images_upload_handler: async (blobInfo) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', blobInfo.blob(), blobInfo.filename());

        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload?skip_watermark=true`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        })
        .then(response => response.json())
        .then(result => {
          if (result.url) {
            resolve(result.url);
          } else {
            reject('Upload failed');
          }
        })
        .catch(err => {
          reject('Upload error: ' + err);
        });
      });
    },

    // Allow pasting images
    paste_data_images: true,
    
    // Image options
    image_advtab: true,
    image_title: true,
    automatic_uploads: true,
    file_picker_types: 'image',

    // Media embed options
    media_live_embeds: true,
    media_url_resolver: (data, resolve) => {
      if (data.url.includes('youtube.com') || data.url.includes('youtu.be')) {
        const videoId = data.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
        if (videoId) {
          resolve({
            html: `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`
          });
          return;
        }
      }
      resolve({ html: '' });
    },

    // Font options
    font_family_formats: 'Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; Georgia=georgia,palatino,serif; Times New Roman=times new roman,times,serif; Verdana=verdana,geneva,sans-serif',
    font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',

    // Block formats (headings, paragraphs)
    block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Preformatted=pre',

    // Code sample languages
    codesample_languages: [
      { text: 'HTML/XML', value: 'markup' },
      { text: 'JavaScript', value: 'javascript' },
      { text: 'CSS', value: 'css' },
      { text: 'Python', value: 'python' },
      { text: 'PHP', value: 'php' },
      { text: 'Ruby', value: 'ruby' },
      { text: 'Java', value: 'java' },
      { text: 'C', value: 'c' },
      { text: 'C#', value: 'csharp' },
      { text: 'C++', value: 'cpp' }
    ],

    // Placeholder
    placeholder: placeholder || 'Start typing your content here...',

    // Track the selected <img> node so the "Style Image" button knows
    // what to operate on (null when the caret/selection isn't on an image).
    setup: (editor) => {
      editor.on('NodeChange SelectionChange', () => {
        const node = editor.selection.getNode();
        setSelectedImageNode(node && node.nodeName === 'IMG' ? node : null);
      });
    },

    // Disable branding
    branding: false,
    
    // Promotion
    promotion: false,

    // Allow all HTML elements and attributes for embeds
    valid_elements: '*[*]',
    extended_valid_elements: 'script[src|async|defer|type|charset],iframe[src|frameborder|style|scrolling|class|width|height|name|align|allowfullscreen]',
    valid_children: '+body[style],+body[script],+body[iframe]',
  };

  return (
    <>
      <div className="advanced-editor-wrapper">
        {/* Custom toolbar buttons */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowImageLibrary(true)}
            className="rounded-none text-xs"
          >
            <ImageIcon className="h-4 w-4 mr-1" />
            Image Library
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowCtaDialog(true)}
            className="rounded-none text-xs"
            data-testid="email-editor-cta-btn"
          >
            <MousePointerClick className="h-4 w-4 mr-1" />
            CTA Button
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!selectedImageNode}
            onClick={() => { setActiveImageNode(selectedImageNode); setShowImageStyle(true); }}
            className="rounded-none text-xs"
            data-testid="style-image-btn"
            title={selectedImageNode ? "Resize, round corners, shadow or crop this image" : "Select an image first"}
          >
            <Sliders className="h-4 w-4 mr-1" />
            Style Image
          </Button>
          
          <div className="text-xs text-muted-foreground ml-auto">
            💡 Use "Code" button for HTML editing • All formatting preserved
          </div>
        </div>

        {/* TinyMCE Editor */}
        <Editor
          apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
          onInit={(evt, editor) => editorRef.current = editor}
          value={value}
          onEditorChange={(content) => onChange(content)}
          init={editorConfig}
        />

        <style>{`
          .tox-tinymce {
            border-radius: 8px;
            border: 1px solid #e2e8f0;
            width: 100% !important;
            max-width: 100% !important;
          }

          .tox .tox-toolbar,
          .tox .tox-toolbar__primary {
            background: #f8f9fa !important;
          }

          .tox .tox-statusbar {
            border-top: 1px solid #e2e8f0;
          }

          .tox .tox-edit-area__iframe {
            background-color: #ffffff;
          }

          /* Make embeds responsive */
          .mce-content-body iframe {
            max-width: 100%;
          }

          .mce-content-body img {
            max-width: 100%;
            height: auto;
          }
        `}</style>
      </div>

      {/* Image Library Modal */}
      <ImageLibraryModal
        open={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelect={handleImageLibrarySelect}
        allowEdit={false}
      />

      {/* Image Style Panel (resize, corners, shadow, crop) */}
      <ImageStylePanel
        open={showImageStyle}
        onClose={() => { setShowImageStyle(false); setActiveImageNode(null); }}
        editor={editorRef.current}
        node={activeImageNode}
      />

      {/* CTA Button Dialog */}
      <Dialog open={showCtaDialog} onOpenChange={setShowCtaDialog}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="email-cta-dialog">
          <DialogHeader>
            <DialogTitle>Insert CTA Button</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="email-cta-text">Button Copy</Label>
              <Input
                id="email-cta-text"
                placeholder="e.g. Book a Viewing"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className="rounded-none"
                data-testid="email-cta-text-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-cta-url">Destination URL</Label>
              <Input
                id="email-cta-url"
                type="url"
                placeholder="https://example.com"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                className="rounded-none"
                data-testid="email-cta-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Preview</Label>
              <div
                className="flex items-center justify-center rounded-none border border-dashed border-muted-foreground/30 p-6 bg-muted/30"
                data-testid="email-cta-preview"
              >
                <span
                  style={{
                    display: "inline-block",
                    padding: "14px 28px",
                    fontFamily: "Arial, Helvetica, sans-serif",
                    fontSize: "15px",
                    fontWeight: "bold",
                    textDecoration: "none",
                    borderRadius: "6px",
                    backgroundColor: siteStyles?.buttonBgHex || "#2f4f4f",
                    color: siteStyles?.buttonTextHex || "#ffffff",
                  }}
                >
                  {ctaText.trim() || "Button Copy"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Matches your site's current button colors — emails can't use theme
                variables, so these exact colors get baked into the button.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCtaDialog(false)}
              className="rounded-none"
              data-testid="email-cta-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={insertCtaButton}
              className="rounded-none"
              data-testid="email-cta-insert-btn"
            >
              Insert CTA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdvancedEditor;
